import React, { useEffect, useState } from 'react';
import { Eye, EyeOff, Check } from 'lucide-react';
import apiService from '../../services/apiServices';
import Dialog from './Dialog';

/**
 * Regjistrimi i kompanisë së re — i njëjti rrjedhë si te landing-u i mëparshëm:
 * dy hapa, identifikuesi i kontrolluar ndërsa shkruhet, fjalëkalimi dy herë,
 * termat, dhe `POST /api/tenants/request`, që e hap kompaninë menjëherë (ose
 * kërkon konfirmim me email, sipas serverit).
 *
 * Tekstet vijnë nga `landingContent.js` → `register` (sq/en/de).
 * `onSignIn(email)` e çon njeriun te kyçja me email-in tashmë të mbushur.
 */

const API_URL = process.env.REACT_APP_API_URL || '';
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMPTY = { contact_person: '', name: '', contact_email: '', phone: '', admin_password: '', admin_password2: '' };

export const slugify = (s) => ((s || '').toString().toLowerCase().trim()
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40)) || '';

export default function RegisterCompanyDialog({ r, closeLabel, onClose, onSignIn }) {
  const [reg, setReg] = useState(EMPTY);
  const [step, setStep] = useState(1);
  const [terms, setTerms] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  // 'checking' | 'free' | 'used' | null
  const [slugState, setSlugState] = useState(null);
  // Përgjigjja e serverit pas hapjes: { email, trialHours, mailSent, needsConfirm }
  const [done, setDone] = useState(null);

  const set = (k) => (e) => {
    const { value } = e.target;
    setReg((f) => ({ ...f, [k]: value }));
  };

  // Identifikuesi derivohet nga emri dhe kontrollohet ndërsa shkruhet, që njeriu
  // ta dijë para dorëzimit nëse emri është i zënë.
  const slug = slugify(reg.name);
  useEffect(() => {
    if (slug.length < 3) { setSlugState(null); return undefined; }
    let alive = true;
    setSlugState('checking');
    const id = setTimeout(() => {
      fetch(`${API_URL}/api/tenants/slug-available?slug=${encodeURIComponent(slug)}`)
        .then((res) => res.json())
        .then((d) => { if (alive) setSlugState(d?.available ? 'free' : 'used'); })
        .catch(() => { if (alive) setSlugState(null); });
    }, 450);
    return () => { alive = false; clearTimeout(id); };
  }, [slug]);

  // Hapi i parë validohet para të dytit: askush nuk arrin te fjalëkalimi me një
  // identifikues të zënë.
  const next = (e) => {
    e.preventDefault();
    if (!reg.contact_person.trim() || !reg.name.trim() || !reg.contact_email.trim() || !reg.phone.trim()) {
      setErr(r.errRequired); return;
    }
    if (!EMAIL.test(reg.contact_email.trim())) { setErr(r.errEmail); return; }
    if (slugState === 'used') { setErr(r.errSlugTaken); return; }
    setErr(''); setStep(2);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!reg.admin_password.trim()) { setErr(r.errRequired); return; }
    // Pa postë që punon, një gabim shtypi te fjalëkalimi e mbyll llogarinë.
    if (reg.admin_password !== reg.admin_password2) { setErr(r.errPasswordMatch); return; }
    if (slugState === 'used') { setErr(r.errSlugTaken); return; }
    if (!terms) { setErr(r.errTerms); return; }
    if (reg.admin_password.length < 8) { setErr(r.errPassword); return; }
    setBusy(true); setErr('');
    try {
      const resp = await apiService().post('tenants/request', {
        name: reg.name.trim(),
        slug,
        contact_email: reg.contact_email.trim(),
        admin_password: reg.admin_password,
        phone: reg.phone.trim() || undefined,
        contact_person: reg.contact_person.trim() || undefined,
      });
      if (resp?.success) {
        // Afati dhe gjendja e postës vijnë nga serveri: vetëm ai e di çfarë u shkrua vërtet.
        setDone({
          email: resp?.data?.email || reg.contact_email.trim(),
          trialHours: resp?.data?.trialHours ?? null,
          mailSent: resp?.data?.confirmationSent ?? null,
          needsConfirm: !!resp?.data?.requiresConfirmation,
        });
      } else {
        setErr(resp?.error || r.errGeneric);
      }
    } catch (ex) {
      setErr(ex?.message || r.errGeneric);
    } finally {
      setBusy(false);
    }
  };

  const badge = done?.trialHours ? `${r.badgePrefix} ${Math.round(done.trialHours / 24)} ${r.badgeDays}` : r.badge;

  if (done) {
    return (
      <Dialog labelledBy="nxp-reg-title" describedBy="nxp-reg-desc" closeLabel={closeLabel} onClose={onClose} focusKey="done">
        <div className="nxp-dialog-done" role="status">
          <span className="nxp-done-mark" aria-hidden="true"><Check size={26} strokeWidth={2.5} /></span>
          <h2 id="nxp-reg-title" className="nxp-h3">{done.needsConfirm ? r.confirmTitle : r.sentTitle}</h2>
          <p id="nxp-reg-desc">{done.needsConfirm ? r.confirmDesc : r.sentDesc}</p>
          {done.needsConfirm && <code className="nxp-code">{done.email}</code>}
          <span className="nxp-badge">{badge}</span>
          {done.mailSent === false && (
            // Kur posta bie, ekrani është i vetmi vend ku njeriu i sheh kredencialet.
            <div className="nxp-warn">
              <b>{r.noMailTitle}</b>
              <span>{r.noMail}</span>
              <code className="nxp-code">{done.email}</code>
            </div>
          )}
          <div className="nxp-cta-row nxp-center-row">
            <button type="button" className="nxp-btn nxp-btn-outline" onClick={onClose} data-autofocus={done.needsConfirm || undefined}>{r.close}</button>
            {!done.needsConfirm && (
              <button type="button" className="nxp-btn nxp-btn-primary" onClick={() => onSignIn(done.email)} data-autofocus>{r.signIn}</button>
            )}
          </div>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog labelledBy="nxp-reg-title" closeLabel={closeLabel} onClose={onClose} wide focusKey={step}>
      <form onSubmit={step === 1 ? next : submit} noValidate>
        <div className="nxp-reg-head">
          <h2 id="nxp-reg-title" className="nxp-h3">{r.title}</h2>
          <span className="nxp-badge">{r.badge}</span>
        </div>
        <ol className="nxp-reg-steps" aria-hidden="true">
          <li className="is-on">{step === 2 ? '✓' : '1'}</li>
          <li className={step === 2 ? 'is-on' : ''}>2</li>
        </ol>

        {step === 1 ? (
          <>
            <label className="nxp-field">
              <span>{r.contactPerson}</span>
              <input type="text" autoComplete="name" placeholder={r.contactPersonPh} value={reg.contact_person} onChange={set('contact_person')} maxLength={120} />
            </label>
            <label className="nxp-field">
              <span>{r.companyNameLabel}</span>
              <input
                type="text"
                autoComplete="organization"
                placeholder={r.companyNamePh}
                value={reg.name}
                onChange={set('name')}
                maxLength={120}
                aria-invalid={slugState === 'used' || undefined}
                aria-describedby={slug.length >= 3 ? 'nxp-reg-slug' : undefined}
              />
            </label>
            {slug.length >= 3 && (
              <p id="nxp-reg-slug" className="nxp-slug" aria-live="polite">
                <span>{r.identifier}</span> <b>{slug}</b>
                {slugState === 'checking' && <span> · {r.slugChecking}</span>}
                {slugState === 'free' && <span className="is-free"> · {r.slugFree}</span>}
                {slugState === 'used' && <span className="is-used"> · {r.slugUsed}</span>}
              </p>
            )}
            <label className="nxp-field">
              <span>{r.emailLabel}</span>
              <input type="email" autoComplete="email" placeholder={r.emailPh} value={reg.contact_email} onChange={set('contact_email')} maxLength={160} />
            </label>
            <label className="nxp-field">
              <span>{r.phoneLabel}</span>
              <input type="tel" autoComplete="tel" placeholder={r.phonePh} value={reg.phone} onChange={set('phone')} maxLength={40} />
            </label>
          </>
        ) : (
          <>
            <label className="nxp-field">
              <span>{r.adminPassword}</span>
              <span className="nxp-pw">
                <input type={showPw ? 'text' : 'password'} autoComplete="new-password" placeholder={r.passwordPh} value={reg.admin_password} onChange={set('admin_password')} maxLength={128} />
                <button type="button" onClick={() => setShowPw((v) => !v)} aria-label={r.showPassword} aria-pressed={showPw}>
                  {showPw ? <EyeOff size={17} aria-hidden="true" /> : <Eye size={17} aria-hidden="true" />}
                </button>
              </span>
            </label>
            <label className="nxp-field">
              <span>{r.adminPassword2}</span>
              <span className="nxp-pw">
                <input
                  type={showPw2 ? 'text' : 'password'}
                  autoComplete="new-password"
                  placeholder={r.password2Ph}
                  value={reg.admin_password2}
                  onChange={set('admin_password2')}
                  maxLength={128}
                  aria-invalid={(reg.admin_password2 && reg.admin_password !== reg.admin_password2) || undefined}
                />
                <button type="button" onClick={() => setShowPw2((v) => !v)} aria-label={r.showPassword} aria-pressed={showPw2}>
                  {showPw2 ? <EyeOff size={17} aria-hidden="true" /> : <Eye size={17} aria-hidden="true" />}
                </button>
              </span>
            </label>
            <label className="nxp-terms">
              <input type="checkbox" checked={terms} onChange={(e) => { setErr(''); setTerms(e.target.checked); }} />
              <span>
                {r.termsPre}{' '}
                <a href="/kushtet/" target="_blank" rel="noreferrer">{r.termsLink}</a>
                {' '}{r.termsAnd}{' '}
                <a href="/privatesia/" target="_blank" rel="noreferrer">{r.privacyLink}</a>.
              </span>
            </label>
            <p className="nxp-hint">{r.verifyNote}</p>
          </>
        )}

        {err && <p className="nxp-error" role="alert">{err}</p>}

        {step === 1 ? (
          <>
            <button type="submit" className="nxp-btn nxp-btn-primary nxp-btn-block">{r.next}</button>
            <p className="nxp-hint nxp-center">
              {r.haveAccount}{' '}
              <button type="button" className="nxp-link" onClick={() => onSignIn(reg.contact_email.trim())}>{r.signInHere}</button>
            </p>
          </>
        ) : (
          <div className="nxp-reg-actions">
            <button type="button" className="nxp-btn nxp-btn-outline" onClick={() => { setErr(''); setStep(1); }}>{r.back}</button>
            <button type="submit" className="nxp-btn nxp-btn-primary" disabled={busy}>{busy ? r.sending : r.send}</button>
          </div>
        )}
      </form>
    </Dialog>
  );
}
